import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fmtDate, fmtDatetime } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Loader2, FileText, Download, Trash2, Pencil } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import CoverRecordDialog from '../../components/horses/CoverRecordDialog';
import StudFeeDialog from '../../components/horses/StudFeeDialog';
import ProduceRecordDialog from '../../components/horses/ProduceRecordDialog';
import ProduceRecordFormDialog from '../../components/horses/ProduceRecordFormDialog';
import BreedingSeasonsCard from '../../components/horses/BreedingSeasonsCard';
import { useConfirm } from '../../components/common/ConfirmDialog';

const BreedingTab = ({ horse, onPregnancyChanged }) => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const qc = useQueryClient();
  const horseId = horse?.id;
  const isFemale = horse?.sex === 'female';
  const isMale   = horse?.sex === 'male';

  // ── Server state ──────────────────────────────────────────────────────────
  const { data: coverData, isLoading: loading } = useQuery({
    queryKey: QK.coverRecords(horseId),
    queryFn: async () => {
      const r = await api.get(`/cover-records?horse_id=${horseId}`);
      return { coverRecords: r.data || [], currentCover: (r.data || []).find(c => c.is_current_cover) || null };
    },
    enabled: !!horseId && isFemale,
  });
  const coverRecords = coverData?.coverRecords ?? [];
  const currentCover = coverData?.currentCover ?? null;

  const { data: studFees = [], isLoading: loadingStudFees } = useQuery({
    queryKey: QK.studFees(horseId),
    queryFn: () => api.get(`/stud-fees?horse_id=${horseId}`).then(r => r.data),
    enabled: !!horseId && isMale,
  });

  const { data: produceRecords = [] } = useQuery({
    queryKey: QK.entityFiles({ horse_id: horseId, category: 'pdf_mare_produce_record' }),
    queryFn: async () => {
      const r = await api.get('/files', { params: { horse_id: horseId } });
      return (r.data || []).filter(f => f.category === 'pdf_mare_produce_record');
    },
    enabled: !!horseId && isFemale,
  });

  const { data: offspring = [] } = useQuery({
    queryKey: QK.offspring(horseId),
    queryFn: () => api.get(`/horses/${horseId}/offspring`).then(r => r.data),
    enabled: !!horseId && isFemale,
  });

  const { data: structuredProduceRecords = [], refetch: refetchStructuredProduce } = useQuery({
    queryKey: QK.breedingProduceRecords(horseId),
    queryFn: () => api.get(`/breeding-seasons/produce-records?horse_id=${horseId}`).then(r => r.data),
    enabled: !!horseId && isFemale,
  });

  const { data: catalogItems = [] } = useQuery({
    queryKey: QK.farmfolioCatalog(),
    queryFn: () => api.get('/farmfolio/care-catalog').then(r => r.data),
    enabled: !!currentUser?.tenant_id,
  });

  const { data: locations = [] } = useQuery({
    queryKey: QK.farmfolioLocations(),
    queryFn: () => api.get('/farmfolio/locations').then(r => r.data),
    enabled: !!currentUser?.tenant_id,
  });

  const { data: tenant } = useQuery({
    queryKey: QK.tenant(currentUser?.tenant_id),
    queryFn: () => api.get(`/tenants/${currentUser.tenant_id}`).then(r => r.data),
    enabled: !!currentUser?.tenant_id,
  });
  const isFarm = tenant?.is_farm === true;

  const invalidateCoverRecords    = () => qc.invalidateQueries({ queryKey: QK.coverRecords(horseId) });
  const invalidateStudFees        = () => qc.invalidateQueries({ queryKey: QK.studFees(horseId) });
  const invalidateProduceRecords  = () => qc.invalidateQueries({ queryKey: QK.entityFiles({ horse_id: horseId, category: 'pdf_mare_produce_record' }) });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [foalProduceDialog, setFoalProduceDialog] = useState(null);
  const [produceFormDialog, setProduceFormDialog] = useState({ open: false, record: null });
  
  const [coverDialogOpen, setCoverDialogOpen] = useState(false);
  const [studFeeDialogOpen, setStudFeeDialogOpen] = useState(false);
  const [produceDialogOpen, setProduceDialogOpen] = useState(false);
  const [coverDetailOpen, setCoverDetailOpen] = useState(false);
  const [editingCover, setEditingCover] = useState(null);
  
  // File preview states
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isEditingFile, setIsEditingFile] = useState(false);
  const [editFileData, setEditFileData] = useState({
    description: '',
    notes: ''
  });

  // Detail view states
  const [selectedCover, setSelectedCover] = useState(null);
  const confirm = useConfirm();

  const handleCoverCreated = () => {
    setCoverDialogOpen(false);
    invalidateCoverRecords();
    toast.success('Cover record saved successfully');
  };

  const handleStudFeeCreated = () => {
    setStudFeeDialogOpen(false);
    invalidateStudFees();
    toast.success('Stud fee saved successfully');
  };

  const handleProduceRecordAdded = () => {
    setProduceDialogOpen(false);
    invalidateProduceRecords();
    toast.success('Produce record uploaded successfully');
  };

  const { userTimezone } = useAuth();
  // fmtDate / fmtDatetime imported from dateUtils — see utils/dateUtils.js for usage rules

  const handleCoverClick = (record, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setSelectedCover(record);
    setCoverDetailOpen(true);
  };

  const handleEditCover = () => {
    setEditingCover(selectedCover);
    setCoverDetailOpen(false);
    setCoverDialogOpen(true);
  };

  const handleProduceClick = async (file, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setPreviewFile(file);
    setEditFileData({
      description: file.description || '',
      notes: file.notes || ''
    });
    setIsEditingFile(false);
    setPreviewOpen(true);
    
    try {
      const response = await api.get(`/files/${file.id}`, {
        responseType: 'blob'
      });
      const url = URL.createObjectURL(response.data);
      setPreviewUrl(url);
    } catch (error) {
      console.error('Error loading file preview:', error);
      toast.error('Failed to load file preview');
    }
  };

  const handleUpdateFile = async () => {
    try {
      const formData = new FormData();
      formData.append('description', editFileData.description);
      formData.append('notes', editFileData.notes);

      await api.put(`/files/${previewFile.id}`, formData);
      
      toast.success('File updated successfully');
      setIsEditingFile(false);
      invalidateProduceRecords();
      
      // Update preview file data
      setPreviewFile({
        ...previewFile,
        description: editFileData.description,
        notes: editFileData.notes
      });
    } catch (error) {
      console.error('Error updating file:', error);
      toast.error('Failed to update file');
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await api.get(`/files/${fileId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Download failed');
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!await confirm({ title: 'Are you sure you want to delete this file?', destructive: true })) return;

    try {
      await api.delete(`/files/${fileId}`);
      toast.success('File deleted successfully');
      invalidateProduceRecords();
      if (previewFile?.id === fileId) {
        setPreviewOpen(false);
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Female Horse - Cover Records */}
      {isFemale && (
        <>
          {/* Current Cover Status */}
          {currentCover && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Current Cover Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Covering Sire:</span>
                    <span className="text-sm font-medium">{currentCover.covering_sire}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Cover Date:</span>
                    <span className="text-sm font-medium">{fmtDate(currentCover.cover_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Cover Outcome:</span>
                    <span className="text-sm font-medium">{currentCover.outcome}</span>
                  </div>
                  {currentCover.pregnancy_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Pregnancy Status:</span>
                      <span className="text-sm font-medium">{currentCover.pregnancy_status}</span>
                    </div>
                  )}
                  {currentCover.fertility_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground">Fertility Status:</span>
                      <span className="text-sm font-medium">{currentCover.fertility_status}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cover History */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Cover History</CardTitle>
                <Button
                  onClick={() => setCoverDialogOpen(true)}
                  variant="ghost"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {coverRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No cover records yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Column Headers */}
                  <div className="grid grid-cols-6 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                    <div>Covering Sire</div>
                    <div>Cover Date</div>
                    <div>Cover Outcome</div>
                    <div>Pregnancy</div>
                    <div>Fertility</div>
                    <div></div>
                  </div>
                  
                  {/* Data Rows */}
                  {coverRecords.map((record) => (
                    <div
                      key={record.id}
                      className="grid grid-cols-6 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={(e) => handleCoverClick(record, e)}
                    >
                      <div>
                        <p className="text-sm font-medium">{record.covering_sire}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">{fmtDate(record.cover_date)}</p>
                      </div>
                      <div>
                        <span className="text-sm font-medium">{record.outcome}</span>
                      </div>
                      <div>
                        {record.pregnancy_status && <span className="text-sm font-medium">{record.pregnancy_status}</span>}
                      </div>
                      <div>
                        {record.fertility_status && <span className="text-sm font-medium">{record.fertility_status}</span>}
                      </div>
                      <div className="flex items-center justify-end">
                        {/* Sold badge — driven by is_sold flag OR horse sold with no pregnancy outcome */}
                        {(record.is_sold
                          || record.pregnancy_status === 'Sold'
                          || (horse.sale_status === 'Sold' && !horse.keep_in_care && !record.pregnancy_status && !record.is_sold)
                        ) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-gray-200 text-gray-700 border border-gray-300">Sold</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Produce Records — PDF uploads for non-FarmFolio tenants */}
          {!isFarm && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Produce Records</CardTitle>
                <Button onClick={() => setProduceDialogOpen(true)} variant="ghost" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {produceRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No produce records yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {produceRecords.map((record) => (
                    <div
                      key={record.id}
                      className="flex items-center justify-between p-3 border-b last:border-b-0 hover:bg-muted/30 transition-colors cursor-pointer"
                      onClick={(e) => handleProduceClick(record, e)}
                    >
                      <div>
                        <p className="text-sm">Date: {fmtDatetime(record.created_at, userTimezone)}</p>
                      </div>
                      <FileText className="w-5 h-5 text-green-600" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          )}

          {/* Produce Records — FarmFolio tenants: structured records with foal link */}
          {isFarm && (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Produce Records</CardTitle>
                <Button onClick={() => setProduceFormDialog({ open: true, record: null })} variant="ghost" size="sm">
                  <Plus className="w-4 h-4 mr-2" /> Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {structuredProduceRecords.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">No produce records yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {['Year','Foal Name','Sire','Sex','Color','Notes',''].map(h => (
                          <th key={h} className="text-left pb-2 pr-4 font-semibold text-xs text-muted-foreground uppercase">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {structuredProduceRecords.map(rec => (
                        <tr key={rec.id} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-2.5 pr-4 text-muted-foreground">{rec.foal_year || '—'}</td>
                          <td className="py-2.5 pr-4 font-medium">
                            {rec.linked_horse_id ? (
                              <span className="text-primary cursor-pointer hover:underline"
                                onClick={() => navigate(`/horses/${rec.linked_horse_id}`)}>
                                {rec.foal_name || '—'}
                              </span>
                            ) : (rec.foal_name || '—')}
                          </td>
                          <td className="py-2.5 pr-4 text-muted-foreground">{rec.sire_name || '—'}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground">{rec.foal_sex || '—'}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground">{rec.color || '—'}</td>
                          <td className="py-2.5 pr-4 text-muted-foreground text-xs truncate max-w-[120px]">{rec.notes || '—'}</td>
                          <td className="py-2.5">
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              title="Edit record"
                              onClick={() => setProduceFormDialog({ open: true, record: rec })}>
                              <Pencil className="w-3.5 h-3.5 text-muted-foreground hover:text-primary" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </>
      )}

      {/* Male Horse - Stud Fees */}
      {isMale && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Stud Fees</CardTitle>
              <Button onClick={() => setStudFeeDialogOpen(true)} variant="ghost" size="sm">
                <Plus className="w-4 h-4 mr-2" /> Add
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {studFees.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground"><p className="text-sm">No stud fees yet</p></div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-4 pb-2 border-b font-semibold text-xs text-muted-foreground uppercase">
                  <div>Year</div><div>Amount</div><div>Guarantee</div>
                </div>
                {studFees.map(fee => (
                  <div key={fee.id} className="grid grid-cols-3 gap-4 p-3 border-b last:border-b-0 hover:bg-muted/30">
                    <p className="text-sm font-medium">{fee.year}</p>
                    <p className="text-sm text-muted-foreground">${fee.amount.toLocaleString()}</p>
                    <p className="text-sm text-muted-foreground">{fee.guarantee || '-'}</p>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialogs */}
      {isFemale && (
        <>
          <CoverRecordDialog
            open={coverDialogOpen}
            onClose={() => {
              setCoverDialogOpen(false);
              setEditingCover(null);
            }}
            onSuccess={handleCoverCreated}
            horse={horse}
            record={editingCover}
            isFarm={isFarm}
          />
          <ProduceRecordDialog
            open={produceDialogOpen}
            onClose={() => setProduceDialogOpen(false)}
            onSuccess={handleProduceRecordAdded}
            horse={horse}
          />

          {/* Per-foal produce record dialog (FarmFolio mode) */}
          {foalProduceDialog && (
            <ProduceRecordDialog
              open={!!foalProduceDialog}
              onClose={() => setFoalProduceDialog(null)}
              onSuccess={() => setFoalProduceDialog(null)}
              horse={foalProduceDialog}
            />
          )}

          {/* Structured produce record form (FarmFolio mode) */}
          <ProduceRecordFormDialog
            open={produceFormDialog.open}
            onClose={() => setProduceFormDialog({ open: false, record: null })}
            onSuccess={() => { setProduceFormDialog({ open: false, record: null }); refetchStructuredProduce(); }}
            horse={horse}
            record={produceFormDialog.record}
            offspring={offspring}
          />
          
          {/* Cover Detail Dialog */}
          <Dialog open={coverDetailOpen} onOpenChange={setCoverDetailOpen}>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Cover Record Details</DialogTitle>
              </DialogHeader>
              {selectedCover && (
                <div className="space-y-3 py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Covering Sire:</span>
                    <span className="font-medium">{selectedCover.covering_sire}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Cover Date:</span>
                    <span className="font-medium">{fmtDate(selectedCover.cover_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Cover Outcome:</span>
                    <span className="font-medium">{selectedCover.outcome}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Current Cover:</span>
                    <span className="font-medium">{selectedCover.is_current_cover ? 'Yes' : 'No'}</span>
                  </div>
                  {selectedCover.pregnancy_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Pregnancy Status:</span>
                      <span className="font-medium">{selectedCover.pregnancy_status}</span>
                    </div>
                  )}
                  {/* Sold note — from is_sold flag or horse sold with no pregnancy outcome */}
                  {(selectedCover.is_sold
                    || selectedCover.pregnancy_status === 'Sold'
                    || (horse.sale_status === 'Sold' && !horse.keep_in_care && !selectedCover.pregnancy_status && !selectedCover.is_sold)
                  ) && (
                    <div className="p-2 rounded bg-gray-100 border border-gray-200">
                      <span className="text-sm text-gray-700 font-medium">
                        {(selectedCover.sold_date || horse.sold_date)
                          ? `Sold on ${new Date((selectedCover.sold_date || horse.sold_date) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}. No further records after this date.`
                          : 'Sold. No further records after this date.'}
                      </span>
                    </div>
                  )}
                  {selectedCover.fertility_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Fertility Status:</span>
                      <span className="font-medium">{selectedCover.fertility_status}</span>
                    </div>
                  )}
                </div>
              )}
              <DialogFooter className="flex items-center justify-between sm:justify-between">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleEditCover}
                  title="Edit record"
                >
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => setCoverDetailOpen(false)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Produce Record Preview Dialog */}
          <Dialog open={previewOpen} onOpenChange={(open) => {
            setPreviewOpen(open);
            if (!open) {
              setIsEditingFile(false);
            }
          }}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                {isEditingFile ? (
                  <div className="flex-1 space-y-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Description / Name</Label>
                      <Input
                        value={editFileData.description}
                        onChange={(e) => setEditFileData({ ...editFileData, description: e.target.value })}
                        placeholder="Enter description"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Notes</Label>
                      <Textarea
                        value={editFileData.notes}
                        onChange={(e) => setEditFileData({ ...editFileData, notes: e.target.value })}
                        placeholder="Additional notes..."
                        rows={2}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <DialogTitle>{previewFile?.description || previewFile?.original_filename}</DialogTitle>
                    {previewFile?.category && (
                      <p className="text-xs text-primary mt-1">Mare Produce Record</p>
                    )}
                    {previewFile?.notes && (
                      <p className="text-xs text-muted-foreground mt-1">{previewFile.notes}</p>
                    )}
                  </>
                )}
              </DialogHeader>
              <div className="flex items-center justify-center p-4 bg-black/5 rounded min-h-[400px]">
                {previewUrl ? (
                  previewFile?.mime_type?.includes('pdf') ? (
                    <iframe 
                      src={previewUrl} 
                      title="PDF Preview"
                      className="w-full h-[60vh] border rounded"
                    />
                  ) : previewFile?.mime_type?.includes('image') ? (
                    <img src={previewUrl} alt={previewFile?.original_filename || 'Preview'} className="max-w-full max-h-[60vh] object-contain rounded" />
                  ) : (
                    <div className="text-center space-y-3">
                      <p className="text-muted-foreground text-sm">Preview not available for this file type.</p>
                      <Button variant="outline" size="sm" onClick={() => handleDownloadFile(previewFile?.id, previewFile?.original_filename || 'file')}>
                        Download to view
                      </Button>
                    </div>
                  )
                ) : (
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                )}
              </div>
              <DialogFooter className="flex justify-between sm:justify-between">
                <div className="flex gap-2">
                  {isEditingFile ? (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsEditingFile(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={handleUpdateFile}>
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => setIsEditingFile(true)}
                        title="Edit file details"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="icon"
                        onClick={() => handleDeleteFile(previewFile?.id)}
                        className="text-destructive hover:text-destructive"
                        title="Delete file"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
                {!isEditingFile && (
                  <Button 
                    onClick={() => handleDownloadFile(previewFile?.id, previewFile?.original_filename)}
                  >
                    <Download className="w-4 h-4 mr-2" /> Download
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}

      {isMale && (
        <StudFeeDialog
          open={studFeeDialogOpen}
          onClose={() => setStudFeeDialogOpen(false)}
          onSuccess={handleStudFeeCreated}
          horse={horse}
        />
      )}

      {/* Breeding Seasons — FarmFolio farm tenants only */}
      {isFarm && (
        <BreedingSeasonsCard
          horse={horse}
          locations={locations}
          catalogItems={catalogItems}
          onCoverAdded={invalidateCoverRecords}
          onPregnancyChanged={onPregnancyChanged}
          isFarm={isFarm}
        />
      )}
    </div>
  );
};

export default BreedingTab;
